import type React from "react";

// Colors are driven by CSS custom properties set by ThemeContext.
// The dark/light palettes live in ThemeContext.tsx.
export const theme = {
  color: {
    bg:            "var(--c-bg)",
    panel:         "var(--c-panel)",
    panelAlt:      "var(--c-panelAlt)",
    border:        "var(--c-border)",
    borderStrong:  "var(--c-borderStrong)",
    text:          "var(--c-text)",
    heading:       "var(--c-heading)",
    muted:         "var(--c-muted)",
    subtle:        "var(--c-subtle)",
    faint:         "var(--c-faint)",
    accent:        "var(--c-accent)",
    accentStrong:  "var(--c-accentStrong)",
    accentText:    "var(--c-accentText)",
    accentBg:      "var(--c-accentBg)",
    // Drawing-sheet roles (ThemeContext.tsx): the "you are here" marker, references and links,
    // the ink of a sheet's frame and title strip.
    mark:          "var(--c-mark)",
    ref:           "var(--c-ref)",
    line:          "var(--c-line)",
    success:      "var(--c-success)",
    successText:   "var(--c-successText)",
    successBg:     "var(--c-successBg)",
    warning:       "var(--c-warning)",
    warningText:   "var(--c-warningText)",
    warningBg:     "var(--c-warningBg)",
    danger:        "var(--c-danger)",
    dangerText:    "var(--c-dangerText)",
    dangerBg:      "var(--c-dangerBg)",
    consultant:    "var(--c-consultant)",
    consultantBg:  "var(--c-consultantBg)",
    auditor:       "var(--c-auditor)",
    auditorBg:     "var(--c-auditorBg)",
    // Risk score bands (theme-aware; light=pastel, dark=muted)
    riskLowBg:     "var(--c-riskLowBg)",
    riskLowText:   "var(--c-riskLowText)",
    riskMedBg:     "var(--c-riskMedBg)",
    riskMedText:   "var(--c-riskMedText)",
    riskHighBg:    "var(--c-riskHighBg)",
    riskHighText:  "var(--c-riskHighText)",
    riskCritBg:    "var(--c-riskCritBg)",
    riskCritText:  "var(--c-riskCritText)",
  },
  // Drawing sheets, not soft boxes: square corners on every surface and control.
  radius: { sm: 3, md: 3, lg: 4 },
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};

// Marks a box as a sheet laid on the grid: hooks/useGridSnap.ts finds it by this custom property
// and puts its edges on the grid lines. Spread into anything that should sit on the paper.
export const onGrid = { "--sheet": "1" } as React.CSSProperties;

// A drawing sheet: crisp ink frame, square corners, no shadow — it lies flat on the
// engineering-paper grid, edges on the lines. <SheetCard> (components/Drawing.tsx) adds the title strip.
export const card: React.CSSProperties = {
  ...onGrid,
  background: "var(--c-panel)",
  border: "1px solid var(--c-line)",
  borderRadius: theme.radius.lg,
  padding: 20,
};

// A count on a tab or a sidebar row: a small square tag ruled in the marker colour.
export const countTag: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  lineHeight: "14px",
  minWidth: 18,
  padding: "0 4px",
  borderRadius: 2,
  border: "1px solid var(--c-mark)",
  color: "var(--c-heading)",
  background: "transparent",
  textAlign: "center",
  fontVariantNumeric: "tabular-nums",
  flexShrink: 0,
};

// Anything that floats above the page — a modal, a drawer, a popover, a menu — is a sheet laid
// on top: the same ink frame, square corners, and a flat offset shadow (--c-sheetShadow, per
// palette) instead of a soft blur. Give a modal its title strip with <SheetCard>.
export const floatingSheet: React.CSSProperties = {
  background: "var(--c-panel)",
  border: "1px solid var(--c-line)",
  borderRadius: 4,
  boxShadow: "var(--c-sheetShadow)",
};

// An empty register or a drop zone: construction lines, the part of the drawing still to do.
export const emptyFrame: React.CSSProperties = {
  ...onGrid,
  border: "1px dashed var(--c-accent)",
  padding: "20px 16px",
  textAlign: "center",
  background: "var(--c-panel)",
};

export const btnPrimary: React.CSSProperties = {
  border: "none",
  borderRadius: theme.radius.sm,
  padding: "9px 18px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  background: "var(--c-accent)",
  color: "#fff",
};

// Secondary / outline button — use for cancel, less-destructive actions
export const btnSecondary: React.CSSProperties = {
  border: "1px solid var(--c-border)",
  borderRadius: theme.radius.sm,
  padding: "9px 18px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
  background: "transparent",
  color: "var(--c-muted)",
};

// Danger / destructive button
export const btnDanger: React.CSSProperties = {
  border: "1px solid var(--c-danger)",
  borderRadius: theme.radius.sm,
  padding: "9px 18px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  background: "transparent",
  color: "var(--c-dangerText)",
};

export const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "var(--c-inputBg)",
  border: "1px solid var(--c-borderStrong)",
  borderRadius: theme.radius.sm,
  padding: "9px 12px",
  color: "var(--c-text)",
  fontSize: 13,
  // Without this a <textarea> renders in the browser's monospace default while the <input>
  // beside it renders in Inter — the two looked like different controls in every modal.
  fontFamily: "inherit",
  boxSizing: "border-box",
  outline: "none",
};

// Standard page wrapper — consistent padding across all views
export const pageContainer: React.CSSProperties = {
  padding: "20px 24px",
};

// Page-level heading + subtitle pattern
export const pageHeading: React.CSSProperties = {
  margin: "0 0 4px",
  fontSize: 20,
  fontWeight: 700,
  color: "var(--c-heading)",
  letterSpacing: "-0.3px",
};

export const pageSubtitle: React.CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: "var(--c-subtle)",
  lineHeight: 1.5,
};

/**
 * The one text scale. Every page picks from these instead of writing its own font size, weight
 * and colour — the registers were written by several hands and had drifted to five heading sizes.
 *
 *   pageHeading / pageSubtitle   the page's own title and the line under it (above)
 *   type.sectionTitle            a heading inside the page: a card, a panel, a group of rows
 *   type.itemTitle               the name of one record in a list or card
 *   type.body                    running text
 *   type.hint                    help text, secondary facts under a field or row
 *   type.meta                    the smallest text: timestamps, counts, footnotes
 *   type.label                   a form field's label and a key in a key/value list
 *   type.eyebrow                 a small uppercase group label above a block (use sparingly)
 *   type.stat / type.statLabel   a summary figure and the words under it
 *   type.ref                     record references and ids (INC-001, A.5.1, CVE ids, LEIs): the brand
 *                                face with tabular numerals, so they line up without changing typeface
 *   type.mono                    machine text only — keys, secrets, pass keys, code, API paths,
 *                                pasted CSV — where every character must be told apart
 *
 * One typeface everywhere: Inter, served from our own origin (index.css), as on the website.
 *
 * Wording rules that go with it: sentence case for every heading, tab, button and label
 * ("Record policy", not "Record Policy"); British spelling; dates as 25 Sep 2026 (fmtDate in
 * lib/format.ts), date-times as 25 Sep 2026, 14:05.
 */
export const type = {
  sectionTitle: { margin: "0 0 8px", fontSize: 15, fontWeight: 700, color: "var(--c-heading)", lineHeight: 1.35 },
  itemTitle:    { fontSize: 14, fontWeight: 600, color: "var(--c-heading)", lineHeight: 1.4 },
  body:         { fontSize: 13, color: "var(--c-text)", lineHeight: 1.55 },
  hint:         { fontSize: 12, color: "var(--c-subtle)", lineHeight: 1.5 },
  meta:         { fontSize: 11, color: "var(--c-faint)", lineHeight: 1.4 },
  label:        { display: "block", marginBottom: 5, fontSize: 12, fontWeight: 600, color: "var(--c-muted)" },
  eyebrow:      { fontSize: 11, fontWeight: 700, color: "var(--c-subtle)", textTransform: "uppercase", letterSpacing: "0.05em" },
  stat:         { fontSize: 22, fontWeight: 700, color: "var(--c-heading)", lineHeight: 1.1, fontVariantNumeric: "tabular-nums" },
  statLabel:    { fontSize: 12, fontWeight: 600, color: "var(--c-text)" },
  ref:          { fontVariantNumeric: "tabular-nums", fontSize: 12 },
  mono:         { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12 },
} satisfies Record<string, React.CSSProperties>;
